import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Theme,
  Typography,
  createTheme,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import React, { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { deleteElement } from "../store/editorSlice";
import { setIsDrawerOpen } from "../store/visualEditor";
import DynamicForm from "./dynamicForm";

interface IVisualFormEditorStyles {
  isDrawerOpen: boolean;
}

const useStyles = makeStyles<Theme, IVisualFormEditorStyles>(
  (theme: Theme) => ({
    container: {
      display: "flex",
      height: "100%",
      overflowY: "auto",
    },
    drawer: {
      height: "100%",
      backgroundColor: theme.palette.grey["100"],
      display: "flex",
      flexDirection: "column",
      alignItems: (props) => (props.isDrawerOpen ? "flex-start" : "center"),
      padding: theme.spacing(1),
    },
    collapseIcon: {
      width: "10px",
      height: "10px",
      margin: "4px",
    },
    hiddenButton: {
      display: "none",
    },
    openButton: {
      width: "10px",
      height: "10px",
    },
    dynamicContent: {
      flexGrow: 1,
    },
    drawerTab: {
      transform: "rotate(-90deg)",
      marginTop: "40px",
    },
  })
);

const getTheme = (theme: Theme) =>
  createTheme({
    ...theme,
    components: {
      MuiTextField: {
        defaultProps: {
          variant: "standard",
        },
      },
    },
    typography: {
      fontSize: 11,
    },
  });

const VisualEditorPanel: React.FC = () => {
  const selectedRoute = useAppSelector(
    (state) => state.developer.edit.editor.selectedRoute
  );
  const isDrawerOpen = useAppSelector(
    (state) => state.developer.edit.visualEditor.isDrawerOpen
  );
  const styles = useStyles({ isDrawerOpen });
  const dispatch = useAppDispatch();

  const handleChangeIsDrawerOpen = useCallback(
    (newState: boolean) => {
      dispatch(setIsDrawerOpen(newState));
    },
    [dispatch]
  );

  const handleConfirmDelete = useCallback(() => {
    dispatch(deleteElement());
  }, [dispatch]);

  return (
    <ThemeProvider theme={getTheme}>
      <div className={styles.drawer}>
        {!isDrawerOpen && (
          <>
            <IconButton
              onClick={() => {
                handleChangeIsDrawerOpen(true);
              }}
              className={isDrawerOpen ? styles.hiddenButton : styles.openButton}
            >
              <ChevronLeftIcon />
            </IconButton>
            <div className={styles.drawerTab}>Properties</div>
          </>
        )}
        {isDrawerOpen && (
          <>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                onClick={() => {
                  handleChangeIsDrawerOpen(false);
                }}
                className={styles.collapseIcon}
              >
                <ChevronRightIcon />
              </IconButton>
              <Typography variant="subtitle1" fontSize={14}>
                Properties
              </Typography>
            </Stack>
            <Divider />
            <Box sx={{ height: 1, width: 1 }}>
              <DynamicForm
                routeToElement={selectedRoute}
                onDelete={handleConfirmDelete}
              />
            </Box>
          </>
        )}
      </div>
    </ThemeProvider>
  );
};

export default VisualEditorPanel;
